import React, { useContext, useEffect, useState } from "react";
import SharedBasedPaymentSchedule from "../../../components/Schedules/Others/SharedBasedPayement";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import { initSchedule } from "../../../utils/data/initData";
import { PL_SCHEDULES } from "../../../utils/data/controlData";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";

const EMPTY_SCHEDULE_DETAILS = {
  costPools: [],
  transactions: [],
  claimLines: [],
  sourceData: [],
  declarations: {},
  totalAdjustment: 0,
};

const SharedBasedPaymentSchedulePage = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);
  const { canEdit: canEditPLSchedule } = useAccessControl(PL_SCHEDULES);
  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState(
    EMPTY_SCHEDULE_DETAILS,
  );
  const [loading, setLoading] = useState(true);

  const saveSchedule = async (currentSchedule, details) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return false;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Share Based Payment",
      scheduleTitle: currentSchedule.name,
      schemaVersion: details.schemaVersion,
      useCostTracker: details.useCostTracker,
      scheduleId: details.scheduleId,
      ya: details.ya,
      basisPeriodStart: details.basisPeriodStart,
      basisPeriodEnd: details.basisPeriodEnd,
      costPools: details.costPools,
      transactions: details.transactions,
      claimLines: details.claimLines,
      declarations: details.declarations,
      totalAdjustment: details.totalAdjustment,
      notes: currentSchedule.notes,
      reviewNotes: currentSchedule.reviewNotes,
      doc: details.doc,
    };

    try {
      setLoading(true);
      const response = await updatePLSchedule(updatedSchedule);

      if (response) {
        handleSuccess("The schedule has been saved successfully.");
        setSaveTrigger((previous) => !previous);
        return true;
      } else {
        handleError(
          "There was a problem saving the schedule. Please contact support if the issue persists.",
        );
        return false;
      }
    } catch (error) {
      handleError(
        error?.message ||
          "There was a problem saving the schedule. Please contact support if the issue persists.",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly.",
        );
        setLoading(false);
        return;
      }

      try {
        const response = await getScheduleData(
          scheduleId,
          "Share_Based_Payment",
        );
        const scheduleData = response.schedule;

        if (scheduleData) {
          const claimLines = Array.isArray(scheduleData.claimLines)
            ? scheduleData.claimLines
            : [];
          setSchedule(initSchedule(scheduleData, "Share-Based Payment"));
          setScheduleDetails({
            schemaVersion: scheduleData.schemaVersion,
            useCostTracker: scheduleData.useCostTracker,
            scheduleId: scheduleData.scheduleId || scheduleId,
            ya: scheduleData.ya ?? company.yearOfAssessment,
            basisPeriodStart:
              scheduleData.basisPeriodStart || company.financialYearStart,
            basisPeriodEnd:
              scheduleData.basisPeriodEnd || company.financialYearEnd,
            costPools: scheduleData.costPools || [],
            transactions: scheduleData.transactions || [],
            claimLines: claimLines.filter(
              (line) => !line.status || line.status !== "autoload",
            ),
            sourceData: [
              ...(Array.isArray(scheduleData.sourceData)
                ? scheduleData.sourceData
                : []),
              ...claimLines.filter((line) => line.status === "autoload"),
            ],
            declarations: scheduleData.declarations || {},
            totalAdjustment: Number(scheduleData.totalAdjustment) || 0,
            doc: scheduleData.doc || "",
          });
        }
      } catch (error) {
        handleError(
          error?.message ||
            "We encountered an issue while fetching the data. Please refresh the page or try again shortly.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [saveTrigger, scheduleId, getScheduleData, company.uen, updatePLSchedule]);

  if (loading) return <ComponentLoading />;

  return (
    <SharedBasedPaymentSchedule
      company={company}
      schedule={schedule}
      scheduleDetails={scheduleDetails}
      saveSchedule={saveSchedule}
      deleteSchedule={deleteSchedule}
      canEditSchedule={canEditPLSchedule}
    />
  );
};

export default SharedBasedPaymentSchedulePage;
